import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { MatSelect } from '@angular/material/select';
import { Subject } from 'rxjs';
import { areElementsIncluded, compareArrays } from 'src/app/utils/array';
import {
    ISegmentDatasourceColumnState,
    ISegmentsData,
    ISegmentsDatasourceColumnDashboardItem,
} from 'src/app/dashboards/common/items/filter/segment-filter/segments';
import { IDashboardItemFactory } from 'src/app/utils/dashboard-item';
import {
    DataLoaderResult,
    IDataLoaderResult,
} from 'src/app/services/data-loader/data-loader-result';
import {
    QueryContextChange,
    QueryContextService,
} from 'src/app/services/query-context/query-context.service';
import { IQueryContextChange } from 'src/app/services/query-context/models/IQueryContext';
import { selectedValuesPill } from 'src/app/utils/ng-zorro';
import { getDatasourceColumnName } from 'src/app/utils/datasource-sql-map/datasource';
import { ConfigService } from 'src/app/services/config.service';

function getDefaultState(): ISegmentDatasourceColumnState {
    return {
        value: {
            options: [],
            operator: 'OR',
        },
        isFilterApplied: false,
        isSplitApplied: false,
    };
}

@Component({
    selector: 'app-segment-filter',
    templateUrl: './segment-filter.component.html',
    styleUrls: ['./segment-filter.component.scss'],
})
export class SegmentFilterComponent implements OnInit, OnDestroy {
    @Input()
    dashboardItemFactory!: IDashboardItemFactory; //DateRangePickerDashboardItemFactory; // IFactory<IFilterCategoryDashboardItem>;
    dashboardItem!: ISegmentsDatasourceColumnDashboardItem;
    get datasourceColumnName() {
        return getDatasourceColumnName(this.dashboardItem.datasourceColumnId);
    }

    @Input()
    dataLoaderEmitter!: EventEmitter<IDataLoaderResult>;

    _state: ISegmentDatasourceColumnState = getDefaultState();

    dataLoaderResult: IDataLoaderResult =
        DataLoaderResult.getLoading<ISegmentsData>(
            { value: new Array<string>() },
            QueryContextService.EMPTY
        );
    segmentsData: ISegmentsData = { value: new Array<string>() };

    queryContextSubscriber = (contextChange: QueryContextChange) => {
        if (contextChange.reason === 'InitialContext') {
            // find in contextChange.context.datasourceColumns item with the same id as this.datasourceColumnId
            const datasourceColumn =
                contextChange.context.datasourceColumns.find((item) => {
                    return item.id === this.dashboardItem.datasourceColumnId;
                });
            // set provided state or default state
            const state = datasourceColumn
                ? datasourceColumn.state
                : getDefaultState();

            this.setupModelFromState(state);

            this.cd.detectChanges();
        }
    };

    dataLoaderSubscriber(dataLoaderResult: IDataLoaderResult) {
        if (dataLoaderResult.status == 'ok') {
            console.log('SegmentsFilterComponent.set.dataLoaderData');
            // console.log(JSON.stringify(dataLoaderResult));
            this.dataLoaderResult = structuredClone(dataLoaderResult);
            this.segmentsData = this.dashboardItem.composeData(
                dataLoaderResult.data,
                dataLoaderResult.queryContext
            );
            // TODO if selectOptions changed
            // problems
            // 1. new selectOptions do not have all of selectOptionsCtrl
            // this.filteredSelectOptions$.next(this.segmentsData.value.slice());
            // this.filterSelectOptions();
            this.availableOptions = this.segmentsData.value.slice();
            // this.setToggleAllCheckboxState();
        }
    }

    private setupModelFromState(state: ISegmentDatasourceColumnState) {
        this._state = state;
        this.selectedOptionsViewModel = this._state.value.options;
        this.selectedOperatorViewModel = this._state.value.operator;
        this.selectedSplitViewModel = this._state.isSplitApplied;
        this.isSplitControlDisabled = !SegmentFilterComponent.canSplitApply(
            this._state.value
        );
        this.selectedExcludeModeViewModel =
            this._state.isExcludeApplied ?? false;
    }

    // @Output()
    // change = new EventEmitter<IQueryContextChange>();
    /** control for the selected selectOption for multi-selection */
    // selectedOptionsCtrl = new FormControl<string[]>([]);
    availableOptions: string[] = [];
    selectedOptionsViewModel: string[] = [];
    selectedOperatorViewModel!: 'OR' | 'AND';
    selectedSplitViewModel!: boolean;
    selectedExcludeModeViewModel!: boolean;
    allValuesSelected: boolean = false;

    enableExcludeMode!: boolean;
    enableSplitMode!: boolean;
    // if default selectedOperator is undefinded than default isSplitControlDisabled is true
    isSplitControlDisabled: boolean = true;

    /** flag which indicates whether the multi-select panel is opened */
    private isOpened: boolean = false;

    /** temporary list loaded every time a dropdown is opened */
    private openedDropdownOptions: any[] = [];

    /** Subject that emits when the component has been destroyed. */
    protected _onDestroy = new Subject<void>();

    @ViewChild('multiSelect', { static: true }) multiSelect!: MatSelect;

    constructor(
        private queryContextService: QueryContextService,
        private cd: ChangeDetectorRef,
        public config: ConfigService
    ) {}

    ngOnInit(): void {
        this.dashboardItem =
            this.dashboardItemFactory.create() as ISegmentsDatasourceColumnDashboardItem;
        this.queryContextService.appliedQueryContextEmitter.subscribe(
            this.queryContextSubscriber
        );
        // set default
        // TODO listener onStateChange
        SegmentFilterComponent.logMessage({
            msg: 'SegmentFilterComponent.ngOnInit.state',
            state: JSON.stringify(this._state),
        });

        this.dataLoaderEmitter.subscribe((result) =>
            this.dataLoaderSubscriber(result)
        );

        this.enableExcludeMode = this.dashboardItem.excludeMode ?? false;
        this.enableSplitMode = !this.enableExcludeMode;

        this.availableOptions = this._state.value.options.slice();

        this.selectedOptionsViewModel = this._state.value.options.slice();
        this.selectedOperatorViewModel = this._state.value.operator;
        this.selectedExcludeModeViewModel =
            this._state.isExcludeApplied ?? false;
        this.selectedSplitViewModel = this._state.isSplitApplied;
    }

    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    static logMessage(message: any) {
        const loggingEnabled = true;
        if (loggingEnabled) {
            console.log(message);
        }
    }

    onModelChange(changes: any): void {
        // UBI-326 Disabled condition
        // ignore if opened
        // if (this.isOpened) {
        //     console.log(
        //         `SegmentFilterComponent.${this.dashboardItem.datasourceColumnId}.onModelChange. Dropdown is opened. Ignoring`
        //     );
        //     return;
        // }
        console.log({
            msg: `SegmentFilterComponent.${this.dashboardItem.datasourceColumnId}.onModelChange`,
            selectedOptionsViewModel: this.selectedOptionsViewModel,
        });
        this.onChange({ options: this.selectedOptionsViewModel });
    }

    onAttributeDropdownToggle(isOpened: boolean) {
        this.isOpened = isOpened;
        if (isOpened) {
            if (this.selectedOptionsViewModel)
                this.openedDropdownOptions =
                    this.selectedOptionsViewModel.slice();
            return;
        }
        // if dropdown closed
        if (!isOpened) {
            SegmentFilterComponent.logMessage({
                msg: 'SegmentFilterComponent.onAttributeDropdownToggle.closed',
                datasourceColumnId: this.dashboardItem.datasourceColumnId,
                state: JSON.stringify(this._state),
                selectedOptions: this.selectedOptionsViewModel,
            });

            // if selected values are different than previous ones
            if (
                this.selectedOptionsViewModel &&
                !compareArrays(
                    this.selectedOptionsViewModel,
                    this.openedDropdownOptions
                )
            ) {
                SegmentFilterComponent.logMessage({
                    msg: 'SegmentFilterComponent.onAttributeDropdownToggle.change',
                    selectedOptions: this.selectedOptionsViewModel,
                });
                this.onChange({ options: this.selectedOptionsViewModel });
            }
        }
    }

    onOperatorSelectionChange(event: MatButtonToggleChange) {
        this.onChange({ operator: event });
    }

    onSplitSelectionChange(value: boolean) {
        this.onChange({ split: value });
    }

    onExcludeModeChange(value: boolean) {
        this.onChange({ exclude: value });
    }

    // main control
    onChange(change: {
        options?: any;
        operator?: any;
        split?: any;
        exclude?: any;
    }) {
        // SegmentFilterComponent.logMessage({ msg: "SegmentFilterComponent.onChange", change });

        if ('options' in change) {
            SegmentFilterComponent.logMessage({
                msg: 'SegmentFilterComponent.onChange.options',
                state: JSON.stringify(this._state),
                value: change.options,
            });
            // jeżeli w state jest właczony operator to emituj zmianę
            if (this.selectedOperatorViewModel !== undefined) {
                SegmentFilterComponent.logMessage({
                    msg: 'SegmentFilterComponent.onChange.options.operatorNotUndefined',
                    state: JSON.stringify(this._state),
                    change,
                    selectedOperator: this.selectedOperatorViewModel,
                });
                this._state = {
                    ...this._state,
                    value: { ...this._state.value, options: change.options },
                    isFilterApplied: SegmentFilterComponent.isFilterApplied({
                        options: this.selectedOptionsViewModel!,
                        operator: this.selectedOperatorViewModel,
                    }),
                };
                let canSplitApply = SegmentFilterComponent.canSplitApply({
                    options: this.selectedOptionsViewModel!,
                    operator: this.selectedOperatorViewModel,
                });

                // jeżeli nie można splitować a kontrolka splitu jest aktywna...
                if (!canSplitApply && this.isSplitControlDisabled == false) {
                    // ...to trzeba ją disablować i
                    this.isSplitControlDisabled = true;
                    // jeżeli split był aktywny to wyemitować deaktywację
                    // TODO najlepiej gdyby po ponownym wyborze options split się samoczynnie włączył
                    if (this.selectedSplitViewModel) {
                        this._state.isSplitApplied = false;
                        const contextChange: IQueryContextChange = {
                            datasourceColumn: {
                                id: this.dashboardItem.datasourceColumnId,
                                state: this._state,
                            },
                        };
                        // this.change.emit({ split: { id: this.dashboardItem.datasourceColumnId, state: this._state } });
                        this.queryContextService.changePendingQueryContext(
                            contextChange
                        );
                    }
                }
                // jeżeli już można splitować a kontrolka splitu jest nieaktywna...
                console.log({
                    canSplitApply,
                    isSplitCOntrolDisabled: this.isSplitControlDisabled,
                });
                if (canSplitApply && this.isSplitControlDisabled == true) {
                    // ...to trzeba ją enablować
                    this.isSplitControlDisabled = false;
                    // jeżeli split jest aktywny to wyemitować aktywację
                    // TODO najlepiej gdyby po ponownym wyborze options split się samoczynnie włączył
                    if (this.selectedSplitViewModel) {
                        this._state.isSplitApplied = true;
                        const contextChange: IQueryContextChange = {
                            datasourceColumn: {
                                id: this.dashboardItem.datasourceColumnId,
                                state: this._state,
                            },
                        };
                        // this.change.emit({ split: { id: this.dashboardItem.datasourceColumnId, state: this._state } });
                        this.queryContextService.changePendingQueryContext(
                            contextChange
                        );
                    }
                }
                const contextChange: IQueryContextChange = {
                    datasourceColumn: {
                        id: this.dashboardItem.datasourceColumnId,
                        state: this._state,
                    },
                };
                // this.change.emit({ filter: { id: this.dashboardItem.datasourceColumnId, state: this._state } });
                this.queryContextService.changePendingQueryContext(
                    contextChange
                );
            }
        }
        // jeżeli zmienił sie operator
        if ('operator' in change) {
            SegmentFilterComponent.logMessage({
                msg: 'SegmentFilterComponent.onChange.operator',
                state: JSON.stringify(this._state),
                operator: change.operator,
            });
            // jeżeli są wybrane options
            this._state.value = {
                ...this._state.value,
                operator: change.operator,
            };
            if (
                change.operator != undefined &&
                this.selectedOptionsViewModel &&
                this.selectedOptionsViewModel.length > 0
            ) {
                if (this.isSplitControlDisabled == true) {
                    this.isSplitControlDisabled = false;
                }
                const contextChange: IQueryContextChange = {
                    datasourceColumn: {
                        id: this.dashboardItem.datasourceColumnId,
                        state: this._state,
                    },
                };
                // this.change.emit({ filter: { id: this.dashboardItem.datasourceColumnId, state: this._state } });
                this.queryContextService.changePendingQueryContext(
                    contextChange
                );
            }
        }
        // jeżeli zmienił się split
        if ('split' in change) {
            SegmentFilterComponent.logMessage({
                msg: 'SegmentFilterComponent.onChange.split',
                state: JSON.stringify(this._state),
                split: change.split,
            });
            this._state = {
                ...this._state,
                isSplitApplied: this.selectedSplitViewModel,
            };
            const contextChange: IQueryContextChange = {
                datasourceColumn: {
                    id: this.dashboardItem.datasourceColumnId,
                    state: { ...this._state },
                },
            };
            // this.change.emit({ split: { id: this.dashboardItem.datasourceColumnId, state: {...this._state} } });
            this.queryContextService.changePendingQueryContext(contextChange);
        }
        // jeżeli zmienił się exclude
        if ('exclude' in change) {
            SegmentFilterComponent.logMessage({
                msg: 'SegmentFilterComponent.onChange.exclude',
                state: JSON.stringify(this._state),
                exclude: change.exclude,
            });
            this._state = {
                ...this._state,
                isExcludeApplied: this.selectedExcludeModeViewModel,
            };
            const contextChange: IQueryContextChange = {
                datasourceColumn: {
                    id: this.dashboardItem.datasourceColumnId,
                    state: { ...this._state },
                },
            };
            // this.change.emit({ split: { id: this.dashboardItem.datasourceColumnId, state: {...this._state} } });
            this.queryContextService.changePendingQueryContext(contextChange);
        }
    }

    public static toSQL(
        columnName: string,
        data: { options: string[]; operator: any }
    ) {
        SegmentFilterComponent.logMessage({
            msg: 'SegmentFilterComponent.toSQL',
            columnName,
            data,
        });
        // different implementation to test
        return this.toSQLUNNEST(columnName, data);
        // return this.toSQLPLAIN(columnName, data);
    }

    public static toSQLUNNEST(
        columnName: string,
        data: { options: string[]; operator: any }
    ) {
        SegmentFilterComponent.logMessage({
            msg: 'SegmentFilterComponent.toSQLUNNEST',
            columnName,
            data,
        });

        const quote = data.options
            .map((val: any) => {
                return `'${val}'`;
            })
            .join(', ');
        if (data.operator == 'AND') {
            return `(SELECT COUNT(*) FROM UNNEST(${columnName}) segment WHERE segment IN (${quote})) = ${data.options.length}`;
        } else if (data.operator == 'OR') {
            return `EXISTS(SELECT 1 FROM UNNEST(${columnName}) segment WHERE segment IN (${quote}))`;
        } else {
            return 'TRUE';
        }
    }

    public static toSQLPLAIN(
        columnName: string,
        data: { options: string[]; operator: any }
    ) {
        SegmentFilterComponent.logMessage({
            msg: 'SegmentFilterComponent.toSQLPLAIN',
            columnName,
            data,
        });

        const quotes = data.options.map((val: any) => {
            return `'${val}' IN UNNEST(${columnName})`;
        });
        if (data.operator == 'AND') {
            return `(${quotes.join(' AND ')})`;
        } else if (data.operator == 'OR') {
            return `(${quotes.join(' OR ')})`;
        } else {
            return 'TRUE';
        }
    }

    public static toSplitSQL(
        columnName: string,
        data?: { options?: string[] }
    ) {
        // sql do splitu zalezy od data.value
        SegmentFilterComponent.logMessage({
            msg: 'SegmentFilterComponent.toSplitSQL',
            columnName,
            data,
        });
        let sql;
        if (data && data.options && data.options.length > 0) {
            const quote = data.options
                .map((val: any) => {
                    return `'${val}'`;
                })
                .join(', ');
            sql = `(SELECT STRING_AGG(segment, ', ') FROM UNNEST(${columnName}) segment WHERE segment IN (${quote}))`;
        } else {
            sql = `ARRAY_TO_STRING(${columnName}, ', ')`;
        }
        SegmentFilterComponent.logMessage({
            msg: 'SegmentFilterComponent.toSplitSQL.generated SQL',
            sql,
        });
        return sql;
        // return `(SELECT COUNT(*) FROM UNNEST(${columnName}) segment WHERE ${baseToString('segment', data.value)}) = ${data.value.length}`;
    }

    public static isFilterApplied(
        data: ISegmentsDatasourceColumnDashboardItem['datasourceColumn']['state']['value']
    ) {
        // data can be undefined!
        if (
            data &&
            'options' in data &&
            data.options!.length > 0 &&
            'operator' in data
        ) {
            return true;
        }
        return false;
    }

    public static isSplitApplied(
        data: ISegmentsDatasourceColumnDashboardItem['datasourceColumn']['state']['value']
    ) {
        // data can be undefined!
        // throw NotI
        if (data.options.length > 0 && data.operator != undefined) {
            return true;
        }
        return false;
    }

    public static canSplitApply(
        data: ISegmentsDatasourceColumnDashboardItem['datasourceColumn']['state']['value']
    ) {
        // data can be undefined!
        // throw NotI
        SegmentFilterComponent.logMessage({
            msg: 'SegmentFilterComponent.canSplitApply',
            data,
        });
        if (data.options.length > 0 && data.operator != undefined) {
            return true;
        }
        return false;
    }

    public selectedValuesPill() {
        if (
            areElementsIncluded(
                this.availableOptions,
                this.selectedOptionsViewModel
            )
        ) {
            this.allValuesSelected = true;
        }
        return selectedValuesPill(
            this.selectedOptionsViewModel,
            this.availableOptions
        );
    }
}
